import React, { useEffect, useMemo, useState } from "react";
import "./LaborSchedulePlanner.css";

const STORAGE_KEY = "payweek_labor_schedule_operating_hours";

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const EMPLOYEE_COLORS = [
  "#22c55e",
  "#3b82f6",
  "#a855f7",
  "#f97316",
  "#ec4899",
  "#14b8a6",
  "#eab308",
  "#ef4444",
];

const defaultOperatingHours = DAYS.reduce((acc, day) => {
  acc[day] = {
    open: "09:00",
    close: "17:00",
  };
  return acc;
}, {});

const LaborSchedulePlanner = () => {
  const [setupMode, setSetupMode] = useState(true);
  const [operatingHours, setOperatingHours] = useState(defaultOperatingHours);

  const [employees, setEmployees] = useState([
    { id: 1, name: "Emp 1", rate: "", color: EMPLOYEE_COLORS[0] },
    { id: 2, name: "Emp 2", rate: "", color: EMPLOYEE_COLORS[1] },
  ]);

  const [selectedEmployeeId, setSelectedEmployeeId] = useState(1);
  const [schedule, setSchedule] = useState({});

  useEffect(() => {
    const savedHours = localStorage.getItem(STORAGE_KEY);

    if (savedHours) {
      setOperatingHours(JSON.parse(savedHours));
      setSetupMode(false);
    }
  }, []);

  const toNumber = (value) => Number(value) || 0;

  const timeToMinutes = (time) => {
    if (!time) return 0;
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };

  const minutesToTime = (minutes) => {
    const normalizedMinutes = ((minutes % 1440) + 1440) % 1440;
    const hours = Math.floor(normalizedMinutes / 60);
    const mins = normalizedMinutes % 60;

    const suffix = hours >= 12 ? "PM" : "AM";
    const displayHour = hours % 12 || 12;

    return `${displayHour}:${String(mins).padStart(2, "0")} ${suffix}`;
  };

  const getDaySlots = (day) => {
    const openMinutes = timeToMinutes(operatingHours[day].open);
    const closeMinutes = timeToMinutes(operatingHours[day].close);

    const startMinutes = Math.max(openMinutes - 60, 0);
    const endMinutes = Math.min(closeMinutes + 60, 1440);

    const slots = [];

    for (let minute = startMinutes; minute < endMinutes; minute += 30) {
      slots.push(minute);
    }

    return slots;
  };

  const handleOperatingHourChange = (day, field, value) => {
    setOperatingHours((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value,
      },
    }));
  };

  const saveOperatingHours = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(operatingHours));
    setSetupMode(false);
  };

  const editOperatingHours = () => {
    setSetupMode(true);
  };

  const addEmployee = () => {
    const newId = Date.now();

    setEmployees((prev) => [
      ...prev,
      {
        id: newId,
        name: `Emp ${prev.length + 1}`,
        rate: "",
        color: EMPLOYEE_COLORS[prev.length % EMPLOYEE_COLORS.length],
      },
    ]);

    setSelectedEmployeeId(newId);
  };

  const updateEmployee = (employeeId, field, value) => {
    setEmployees((prev) =>
      prev.map((emp) =>
        emp.id === employeeId ? { ...emp, [field]: value } : emp
      )
    );
  };

  const removeEmployee = (employeeId) => {
    if (employees.length <= 1) return;

    setEmployees((prev) => prev.filter((emp) => emp.id !== employeeId));

    setSchedule((prev) => {
      const updated = { ...prev };

      DAYS.forEach((day) => {
        if (updated[day]) {
          delete updated[day][employeeId];
        }
      });

      return updated;
    });

    if (selectedEmployeeId === employeeId) {
      const nextEmployee = employees.find((emp) => emp.id !== employeeId);
      if (nextEmployee) setSelectedEmployeeId(nextEmployee.id);
    }
  };

  const toggleSlot = (day, employeeId, slotMinute) => {
    if (selectedEmployeeId !== employeeId) {
      setSelectedEmployeeId(employeeId);
      return;
    }

    setSchedule((prev) => {
      const daySchedule = prev[day] || {};
      const employeeSlots = daySchedule[employeeId] || [];

      const alreadySelected = employeeSlots.includes(slotMinute);

      const updatedSlots = alreadySelected
        ? employeeSlots.filter((slot) => slot !== slotMinute)
        : [...employeeSlots, slotMinute];

      return {
        ...prev,
        [day]: {
          ...daySchedule,
          [employeeId]: updatedSlots,
        },
      };
    });
  };

  const getEmployeeHours = (day, employeeId) => {
    return ((schedule[day]?.[employeeId] || []).length * 0.5);
  };

  const getEmployeeCost = (day, employee) => {
    return getEmployeeHours(day, employee.id) * toNumber(employee.rate);
  };

  const calculations = useMemo(() => {
  const dailyTotals = {};
  let weeklyCost = 0;
  let weeklyHours = 0;

  DAYS.forEach((day) => {
    let dayCost = 0;
    let dayHours = 0;

    employees.forEach((employee) => {
      const hours =
        ((schedule[day]?.[employee.id] || []).length * 0.5);

      const cost = hours * toNumber(employee.rate);

      dayHours += hours;
      dayCost += cost;
    });

    dailyTotals[day] = {
      hours: dayHours,
      cost: dayCost,
    };

    weeklyHours += dayHours;
    weeklyCost += dayCost;
  });

  return {
    dailyTotals,
    weeklyHours,
    weeklyCost,
  };
}, [schedule, employees]);

  const formatCurrency = (value) =>
    `$${Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  if (setupMode) {
    return (
      <div className="labor-planner-page">
        <div className="labor-planner-topbar">
          <a href="/" className="labor-planner-back-btn">
            ← Back to Home
          </a>
        </div>

        <div className="labor-planner-setup-card">
          <div className="labor-planner-header">
            <h1>Labor Schedule Planner</h1>
            <p>First, enter your operating hours for each day.</p>
          </div>

          <div className="labor-hours-grid">
            {DAYS.map((day) => (
              <div className="labor-hours-row" key={day}>
                <strong>{day}</strong>

                <label>
                  Open
                  <input
                    type="time"
                    value={operatingHours[day].open}
                    onChange={(e) =>
                      handleOperatingHourChange(day, "open", e.target.value)
                    }
                  />
                </label>

                <label>
                  Close
                  <input
                    type="time"
                    value={operatingHours[day].close}
                    onChange={(e) =>
                      handleOperatingHourChange(day, "close", e.target.value)
                    }
                  />
                </label>
              </div>
            ))}
          </div>

          <button
            type="button"
            className="labor-primary-btn"
            onClick={saveOperatingHours}
          >
            Save Operating Hours
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="labor-planner-page">
      <div className="labor-planner-topbar">
        <a href="/" className="labor-planner-back-btn">
          ← Back to Home
        </a>

        <button
          type="button"
          className="labor-edit-hours-btn"
          onClick={editOperatingHours}
        >
          Edit Operating Hours
        </button>
      </div>

      <div className="labor-planner-header">
        <h1>Labor Schedule Planner</h1>
        <p>
          Select an employee, then click time blocks to build the weekly labor schedule.
        </p>
      </div>

      <div className="labor-planner-layout">
        <aside className="labor-employees-panel">
          <div className="labor-panel-title">
            <h2>Employees</h2>
            <button type="button" onClick={addEmployee}>
              + Add
            </button>
          </div>

          {employees.map((employee) => (
            <div
              className={`labor-employee-card ${
                selectedEmployeeId === employee.id ? "active" : ""
              }`}
              key={employee.id}
              onClick={() => setSelectedEmployeeId(employee.id)}
            >
              <div
                className="labor-employee-color"
                style={{ background: employee.color }}
              />

              <div className="labor-employee-fields">
                <input
                  type="text"
                  value={employee.name}
                  onChange={(e) =>
                    updateEmployee(employee.id, "name", e.target.value)
                  }
                />

                <input
                  type="number"
                  value={employee.rate}
                  onChange={(e) =>
                    updateEmployee(employee.id, "rate", e.target.value)
                  }
                  placeholder="Hourly rate"
                />
              </div>

              {employees.length > 1 && (
                <button
                  type="button"
                  className="labor-remove-employee"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeEmployee(employee.id);
                  }}
                >
                  ×
                </button>
              )}
            </div>
          ))}

          <div className="labor-week-total-card">
            <span>Total Week Hours</span>
            <strong>{calculations.weeklyHours.toFixed(2)}</strong>

            <span>Total Week Cost</span>
            <strong>{formatCurrency(calculations.weeklyCost)}</strong>
          </div>
        </aside>

        <main className="labor-schedule-area">
          {DAYS.map((day) => {
            const slots = getDaySlots(day);

            return (
              <section className="labor-day-card" key={day}>
                <div className="labor-day-header">
                  <div>
                    <h2>{day}</h2>
                    <p>
                      {minutesToTime(timeToMinutes(operatingHours[day].open))} -{" "}
                      {minutesToTime(timeToMinutes(operatingHours[day].close))}
                    </p>
                  </div>

                  <div className="labor-day-total">
                    <span>{calculations.dailyTotals[day].hours.toFixed(2)} hrs</span>
                    <strong>{formatCurrency(calculations.dailyTotals[day].cost)}</strong>
                  </div>
                </div>

                <div className="labor-time-labels">
                  {slots.map((slot, index) =>
                    index % 2 === 0 ? (
                      <span key={slot}>{minutesToTime(slot)}</span>
                    ) : (
                      <span key={slot} />
                    )
                  )}
                </div>

                {employees.map((employee) => {
                  const selectedSlots = schedule[day]?.[employee.id] || [];
                  const empHours = getEmployeeHours(day, employee.id);
                  const empCost = getEmployeeCost(day, employee);

                  return (
                    <div
                      className={`labor-employee-schedule-row ${
                        selectedEmployeeId === employee.id ? "selected" : ""
                      }`}
                      key={employee.id}
                    >
                      <div className="labor-row-name">
                        <button
                          type="button"
                          onClick={() => setSelectedEmployeeId(employee.id)}
                        >
                          <span
                            style={{ background: employee.color }}
                          />
                          {employee.name}
                        </button>
                      </div>

                      <div className="labor-time-bar">
                        {slots.map((slot) => {
                          const isSelected = selectedSlots.includes(slot);

                          return (
                            <button
                              type="button"
                              key={slot}
                              className={`labor-time-block ${
                                isSelected ? "active" : ""
                              }`}
                              style={{
                                background: isSelected
                                  ? employee.color
                                  : undefined,
                                borderColor:
                                  selectedEmployeeId === employee.id
                                    ? employee.color
                                    : undefined,
                              }}
                              onClick={() =>
                                toggleSlot(day, employee.id, slot)
                              }
                              title={`${employee.name} - ${minutesToTime(slot)}`}
                            />
                          );
                        })}
                      </div>

                      <div className="labor-row-total">
                        <span>{empHours.toFixed(2)} hrs</span>
                        <strong>{formatCurrency(empCost)}</strong>
                      </div>
                    </div>
                  );
                })}
              </section>
            );
          })}
        </main>
      </div>
    </div>
  );
};

export default LaborSchedulePlanner;